var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getErrorMessage } from '../../../../backend/utils/getErrorMessage';
import { ContactManagementConfigContext } from '../../../../ui/providers';
import { FormConfigContext } from '@juvare/common-form-ui/FormConfigContext';
import { defaultFormWidgets } from '@juvare/common-form-ui/WidgetForm';
import { ClassifierAdminPage, SlideoutFields } from '@juvare/platform-sdk-react/ui/features/classifiers';
import { capitalize } from '@mui/material';
import React from 'react';
import { generateValidateFunction } from './ValidationFunctionGenerator';
function getEntityTitle(row) {
    return row.columns.name;
}
const EntityDetailsComponent = (props) => {
    var _a;
    return (React.createElement(SlideoutFields, { fields: [
            { label: 'Status', value: capitalize(props.response.data.status) },
            { label: 'Title', value: props.response.data.title },
            { label: 'Display Order', value: (_a = props.response.data.displayOrder) !== null && _a !== void 0 ? _a : React.createElement("em", null, "(not set)") },
        ] }));
};
function getFormConfig(additionalPages) {
    return {
        type: 'form',
        children: [
            {
                type: 'form.text',
                key: 'name',
                label: 'Name',
                validate: {
                    required: true,
                },
            },
            {
                type: 'form.text',
                key: 'title',
                label: 'Title',
                validate: {
                    required: true,
                },
            },
            {
                type: 'form.select',
                key: 'status',
                label: 'Status',
                validate: {
                    required: true,
                },
                values: [
                    {
                        value: 'active',
                        label: 'Active',
                    },
                    {
                        value: 'deprecated',
                        label: 'Deprecated',
                    },
                    {
                        value: 'retired',
                        label: 'Retired',
                    },
                ],
            },
            {
                type: 'form.number',
                key: 'displayOrder',
                label: 'Display Order',
            },
            {
                type: 'form.text',
                key: 'description',
                label: 'Description',
                emptyAsNull: true,
            },
            {
                type: 'form.text',
                key: 'key',
                label: 'Key',
                emptyAsNull: true,
            },
            {
                type: 'form.content',
                text: 'Section will not be visible in any page, select which pages this section should be visible in.',
                hidden: {
                    custom: (handler) => {
                        var _a;
                        const data = (_a = handler.up().down('showInPages')) === null || _a === void 0 ? void 0 : _a.getData();
                        return data && data.length > 0;
                    },
                },
                style: {
                    alert: 'warning',
                },
            },
            {
                type: 'form.checkboxList',
                key: 'showInPages',
                label: 'Show In Pages',
                values: [
                    { value: 'public-registration', label: 'Public Registration' },
                    { value: 'login-completion', label: 'Login Completion' },
                    { value: 'new', label: 'New' },
                    { value: 'details', label: 'Details' },
                    { value: 'transfer', label: 'Transfer' },
                    { value: 'profile', label: 'Profile' },
                    ...(additionalPages !== null && additionalPages !== void 0 ? additionalPages : []),
                ],
            },
            {
                type: 'form.text',
                key: 'dataField',
                label: 'Data Field',
                emptyAsNull: true,
            },
            {
                type: 'form.checkbox',
                key: 'autoSaveForm',
                label: 'Automatically save the form when changes are made',
                description: 'You should enable this flag if form is composed of modal dialogs. ' +
                    'Note that auto save needs to be enabled for form components for this section. ' +
                    'If the form is composed of modals, then enable auto save only for modals themselves, do not enable auto save for components inside modals. ' +
                    'In the future all sections should be converted to use auto save, then this confusing flag with a confusing description will be removed.',
            },
            {
                type: 'form.checkbox',
                key: 'generateValidateCompletion',
                label: 'Use Form Rule - Require This Field For Profile Completion',
            },
            {
                type: 'form.text',
                key: 'validateCompletion',
                label: 'Validate Completion (JavaScript)',
                multiline: true,
                rows: 10,
                emptyAsNull: true,
                validate: {
                    custom: (js) => {
                        try {
                            Function('"use strict"; return (function(data) { ' + js + '; })')();
                            return undefined;
                        }
                        catch (error) {
                            return 'Invalid JavaScript code: ' + getErrorMessage(error);
                        }
                    },
                },
                style: {
                    monospace: true,
                },
            },
            {
                type: 'form.checkbox',
                key: 'generateValidateLogin',
                label: 'Use Form Rule - Require This Field For Login',
            },
            {
                type: 'form.text',
                key: 'validateLogin',
                label: 'Validate Login (JavaScript)',
                multiline: true,
                rows: 10,
                emptyAsNull: true,
                validate: {
                    custom: (js) => {
                        try {
                            Function('"use strict"; return (function(data) { ' + js + '; })')();
                            return undefined;
                        }
                        catch (error) {
                            return 'Invalid JavaScript code: ' + getErrorMessage(error);
                        }
                    },
                },
                style: {
                    monospace: true,
                },
            },
        ],
    };
}
const csvFileHeader = ['ID', 'Name', 'Title', 'Status', 'Display Order', 'Description', 'Key', 'Show In Pages', 'Data Field'];
function mapCsvRowToData(row, additionalPageTypes) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const id = ((_a = row.data[0]) !== null && _a !== void 0 ? _a : '').trim();
    const name = ((_b = row.data[1]) !== null && _b !== void 0 ? _b : '').trim();
    const title = ((_c = row.data[2]) !== null && _c !== void 0 ? _c : '').trim();
    const status = ((_d = row.data[3]) !== null && _d !== void 0 ? _d : '').trim().toLowerCase();
    const displayOrder = ((_e = row.data[4]) !== null && _e !== void 0 ? _e : '').trim();
    const description = ((_f = row.data[5]) !== null && _f !== void 0 ? _f : '').trim();
    const key = ((_g = row.data[6]) !== null && _g !== void 0 ? _g : '').trim();
    const showInPages = ((_h = row.data[7]) !== null && _h !== void 0 ? _h : '').trim();
    const dataField = ((_j = row.data[8]) !== null && _j !== void 0 ? _j : '').trim();
    if (name.length === 0) {
        row.errors.push('Name is required');
    }
    if (title.length === 0) {
        row.errors.push('Title is required');
    }
    if (status.length === 0) {
        row.errors.push('Status is required');
    }
    switch (status) {
        case 'active':
        case 'deprecated':
        case 'retired':
            break;
        default:
            row.errors.push('Invalid status, expected one of: active, deprecated, retired');
    }
    let showInPagesArray;
    if (showInPages) {
        showInPagesArray = showInPages.split(';');
        showInPagesArray.forEach((item) => {
            if (!['public-registration', 'login-completion', 'new', 'details', 'transfer', 'profile', ...additionalPageTypes].includes(item)) {
                row.errors.push('Page type is not recognised. Expected type to be one of: public-registration, login-completion, new, details, transfer, profile');
            }
        });
    }
    let displayOrderNumber;
    if (displayOrder) {
        displayOrderNumber = parseFloat(displayOrder);
        if (!isFinite(displayOrderNumber)) {
            row.errors.push('Invalid display number, expected a number');
            displayOrderNumber = undefined;
        }
    }
    if (row.errors.length === 0) {
        row.object = {
            id: id,
            data: {
                name: name,
                title: title,
                status: status,
                displayOrder: displayOrderNumber,
                description: description,
                key: key,
                showInPages: showInPagesArray,
                dataField: dataField,
            },
        };
    }
    return Promise.resolve(row);
}
function mapDataToCsvRow(response) {
    return __awaiter(this, void 0, void 0, function* () {
        return [
            response.id,
            response.data.name,
            response.data.title,
            response.data.status,
            response.data.displayOrder != null ? response.data.displayOrder.toString() : '',
            response.data.description != null ? response.data.description : '',
            response.data.key != null ? response.data.key : '',
            response.data.showInPages != null ? response.data.showInPages.join(';') : '',
            response.data.dataField != null ? response.data.dataField : '',
        ];
    });
}
export const AccountSectionAdminPage = () => {
    var _a, _b;
    const { widgets } = React.useContext(FormConfigContext);
    const { accountConfig } = React.useContext(ContactManagementConfigContext);
    const additionalPages = (_a = accountConfig === null || accountConfig === void 0 ? void 0 : accountConfig.additionalAccountSectionPages) !== null && _a !== void 0 ? _a : [];
    const additionalPageTypes = ((_b = accountConfig === null || accountConfig === void 0 ? void 0 : accountConfig.additionalAccountSectionPages) !== null && _b !== void 0 ? _b : []).map((page) => page.value);
    const formConfig = getFormConfig(additionalPages);
    return (React.createElement(ClassifierAdminPage, { entity: 'accountSection', title: 'Account Section', fullTextSearchField: 'search', getEntityTitle: getEntityTitle, entityDetailsComponent: EntityDetailsComponent, entityDetailsEditForm: true, entityDetailsPreviewForm: true, formConfig: formConfig, csvFileHeader: csvFileHeader, mapCsvRowToData: (row) => mapCsvRowToData(row, additionalPageTypes), mapDataToCsvRow: mapDataToCsvRow, onFormSubmit: (form, response) => generateValidateFunctions(form, response, widgets !== null && widgets !== void 0 ? widgets : defaultFormWidgets) }));
};
function generateValidateFunctions(form, response, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
widgets) {
    const data = {
        form: form,
    };
    if (response.data.generateValidateCompletion) {
        data.validateCompletion = generateValidateFunction(form, widgets, 'validateAccountCompletion');
    }
    if (response.data.generateValidateLogin) {
        data.validateLogin = generateValidateFunction(form, widgets, 'validateAccountLogin');
    }
    return data;
}
